import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/contexts/AuthContext';
import userStorage from '@/lib/userStorage';
import { OnboardingChoice } from '@/components/home/OnboardingChoice';
import { PendingInvitations } from '@/components/home/PendingInvitations';
import { SingleOrgForm } from '@/components/home/SingleOrgForm';
import { MultiOrgForm } from '@/components/home/MultiOrgForm';
import { AcoList } from '@/pages/home/AcoList';
import { useProgramAccess } from '@/platform/ProgramAccessContext';
import { setHomeProgram } from '@/platform/activeProgram';
import { useAco } from '@/contexts/AcoContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Loader2, Building, Plus, MoreVertical, Search, ChevronRight, List, LayoutGrid, Info, ArrowRight, ArrowLeft, Landmark, BarChart3, Layers, Users } from 'lucide-react';
import { TeamAccessSheet } from '@/components/home/TeamAccessSheet';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Define the structure of a client and practice for TypeScript
interface Practice {
  id: number;
  tin: string;
  tin_name: string;
  practice_alias?: string;
}

interface Client {
  id: number;
  client_alias: string;
  account_id?: string;
  practices: Practice[];
}

// ClientListView component with search, keyboard navigation, and expandable practices
interface ClientListViewProps {
  clients: Client[];
  onSelectClient: (client: Client) => void;
  onRegisterNew: () => void;
  onManageOrg: (clientId: number) => void;
}

function ClientListView({ clients, onSelectClient, onRegisterNew, onManageOrg, onSelectPractice }: ClientListViewProps & { onSelectPractice: (client: Client, practice: Practice) => void }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Filter clients based on search term
  const filteredClients = useMemo(() => {
    if (!searchTerm.trim()) return clients;
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    return clients.filter(client => {
      // Search organization fields
      if (client.client_alias?.toLowerCase().includes(searchLower)) return true;
      if (client.account_id?.toLowerCase().includes(searchLower)) return true;
      
      // Search within practices
      return client.practices.some(practice => 
        practice.tin?.toLowerCase().includes(searchLower) ||
        practice.tin_name?.toLowerCase().includes(searchLower) ||
        practice.practice_alias?.toLowerCase().includes(searchLower)
      );
    });
  }, [clients, searchTerm]);

  // Reset selected index when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredClients.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredClients[selectedIndex]) {
          onSelectClient(filteredClients[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setSearchTerm('');
        searchInputRef.current?.blur();
        break;
    }
  }, [filteredClients, selectedIndex, onSelectClient]);

  // Scroll selected item into view
  useEffect(() => {
    const selectedElement = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  // Focus search once when the list first appears — not on every Home remount
  // (re-focus + scrollIntoView felt like a full reload).
  useEffect(() => {
    searchInputRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-3xl mx-auto" onKeyDown={handleKeyDown}>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Select an Organization</h1>
        <p className="text-muted-foreground mt-2">Choose an organization to continue.</p>
      </div>
      
      {/* Search Bar, View Toggle, and Register Button */}
      <div className="flex gap-3 mb-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            type="text"
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-border"
          />
        </div>
        
        {/* View Mode Toggle */}
        <ToggleGroup type="single" value={viewMode} onValueChange={(v) => v && setViewMode(v as 'list' | 'grid')}>
          <ToggleGroupItem value="list" aria-label="List view" className="h-11 w-11 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
            <List className="h-4 w-4" />
          </ToggleGroupItem>
          <ToggleGroupItem value="grid" aria-label="Grid view" className="h-11 w-11 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
            <LayoutGrid className="h-4 w-4" />
          </ToggleGroupItem>
        </ToggleGroup>
        
        <Button 
          onClick={onRegisterNew}
          className="h-11 px-4"
        >
          <Plus className="mr-2 h-4 w-4" />
          Register New
        </Button>
      </div>

      {/* Results Count */}
      {searchTerm && (
        <p className="text-sm text-muted-foreground mb-3">
          Showing {filteredClients.length} of {clients.length} organization{clients.length !== 1 ? 's' : ''}
          {filteredClients.length === 0 && (
            <span className="ml-1">— try a different search term</span>
          )}
        </p>
      )}

      {/* Keyboard hint */}
      {filteredClients.length > 0 && (
        <p className="text-xs text-muted-foreground mb-3">
          <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">↑↓</kbd> navigate
          <span className="mx-2">•</span>
          <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">Enter</kbd> select
          <span className="mx-2">•</span>
          Hover on practice count to see details
        </p>
      )}
      
      {/* Organization List - List View */}
      {viewMode === 'list' && (
        <div className="space-y-2" ref={listRef}>
          {filteredClients.map((client, index) => {
            const isSelected = index === selectedIndex;
            const hasMultiplePractices = client.practices.length > 1;
            
            return (
              <div key={client.id} data-index={index}>
                <div
                  className={`group relative flex items-center justify-between p-4 bg-card border rounded-lg transition-all duration-200 cursor-pointer
                    ${isSelected ? 'bg-accent/40 border-primary/30' : 'hover:bg-accent/30'}
                  `}
                  onClick={() => onSelectClient(client)}
                >
                  {/* Left side - Icon and Info */}
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Building className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium text-base">{client.client_alias}</h3>
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        {hasMultiplePractices ? (
                          <HoverCard openDelay={100} closeDelay={100}>
                            <HoverCardTrigger asChild>
                              <button
                                className="inline-flex items-center gap-1 text-primary hover:underline underline-offset-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {client.practices.length} practices
                                <Info className="h-3.5 w-3.5" />
                              </button>
                            </HoverCardTrigger>
                            <HoverCardContent 
                              className="w-80 p-0" 
                              align="start"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="px-4 py-3 border-b bg-muted/30">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                  Practices in this Organization
                                </p>
                              </div>
                              <div className="max-h-64 overflow-y-auto">
                                {client.practices.map((practice, pIndex) => (
                                  <button
                                    key={practice.id}
                                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/50 transition-colors text-left
                                      ${pIndex !== client.practices.length - 1 ? 'border-b' : ''}
                                    `}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onSelectPractice(client, practice);
                                    }}
                                  >
                                    <div className="flex-shrink-0 w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                                      <Building className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-sm truncate">
                                        {practice.practice_alias || practice.tin_name}
                                      </p>
                                      {practice.tin && (
                                        <p className="text-xs text-muted-foreground">
                                          TIN: {practice.tin}
                                        </p>
                                      )}
                                    </div>
                                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                  </button>
                                ))}
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                        ) : (
                          <>
                            <span>1 practice</span>
                            {client.practices[0]?.tin && (
                              <span className="ml-1">• TIN: {client.practices[0].tin}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Right side - Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className={`transition-opacity duration-200 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectClient(client);
                      }}
                    >
                      Select
                    </Button>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            onManageOrg(client.id);
                          }}
                        >
                          Manage Organization
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Organization Grid - Card View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" ref={listRef}>
          {filteredClients.map((client, index) => {
            const isSelected = index === selectedIndex;
            const hasMultiplePractices = client.practices.length > 1;
            
            return (
              <Card
                key={client.id}
                data-index={index}
                className={`group cursor-pointer transition-all duration-200 hover:shadow-md
                  ${isSelected ? 'bg-accent/40 border-primary/30 shadow-sm' : 'hover:bg-accent/20'}
                `}
                onClick={() => onSelectClient(client)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-3">
                      <Building className="h-6 w-6 text-primary" />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            onManageOrg(client.id);
                          }}
                        >
                          Manage Organization
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <CardTitle className="text-lg">{client.client_alias}</CardTitle>
                  {hasMultiplePractices ? (
                    <HoverCard openDelay={100} closeDelay={100}>
                      <HoverCardTrigger asChild>
                        <button
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline underline-offset-2 w-fit"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {client.practices.length} practices
                          <Info className="h-3.5 w-3.5" />
                        </button>
                      </HoverCardTrigger>
                      <HoverCardContent 
                        className="w-80 p-0" 
                        align="start"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="px-4 py-3 border-b bg-muted/30">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            Practices in this Organization
                          </p>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          {client.practices.map((practice, pIndex) => (
                            <button
                              key={practice.id}
                              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/50 transition-colors text-left
                                ${pIndex !== client.practices.length - 1 ? 'border-b' : ''}
                              `}
                              onClick={(e) => {
                                e.stopPropagation();
                                onSelectPractice(client, practice);
                              }}
                            >
                              <div className="flex-shrink-0 w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                                <Building className="h-4 w-4 text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">
                                  {practice.practice_alias || practice.tin_name}
                                </p>
                                {practice.tin && (
                                  <p className="text-xs text-muted-foreground">
                                    TIN: {practice.tin}
                                  </p>
                                )}
                              </div>
                              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            </button>
                          ))}
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  ) : (
                    <CardDescription>1 practice</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="pt-0">
                  {/* Show first 3 practices (only for single practice or as preview) */}
                  {!hasMultiplePractices && (
                    <div className="space-y-2">
                      {client.practices.slice(0, 1).map((practice) => (
                        <div key={practice.id} className="flex items-center gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary/50" />
                          <span className="text-muted-foreground truncate">
                            {practice.practice_alias || practice.tin_name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
        
      {/* Empty state */}
      {filteredClients.length === 0 && searchTerm && (
        <div className="text-center py-12 text-muted-foreground">
          <Search className="h-12 w-12 mx-auto mb-4 opacity-20" />
          <p className="text-lg font-medium">No organizations found</p>
          <p className="text-sm mt-1">Try searching by organization name, TIN, or practice name</p>
        </div>
      )}
    </div>
  );
}

/**
 * Shown above the ACO / practice list when the user holds both programs.
 * Quiet text row + faint divider — discoverable without competing with the
 * page header (owner 2026-07-21).
 */
function SwitchProgramControl({
  current,
  onSwitch,
  onOpenTeam,
}: {
  current: 'mips' | 'app-plus';
  /** Omitted for single-program users (no switch link). */
  onSwitch?: () => void;
  /** Team & Access is cross-program (one invite grants practices + ACOs), so it
   *  lives here as a peer of Switch program (owner decision 2026-07-31). Omitted
   *  when the user manages no organization. */
  onOpenTeam?: () => void;
}) {
  const label = current === 'app-plus' ? 'APP Plus (MSSP)' : 'MIPS Registry';
  return (
    <div className="max-w-3xl mx-auto mb-5">
      <div className="flex items-center justify-between gap-3 pb-3">
        <p className="text-sm text-muted-foreground">
          Working in <span className="font-medium text-foreground">{label}</span>
        </p>
        <div className="flex items-center gap-4">
          {onOpenTeam && (
            <button
              type="button"
              onClick={onOpenTeam}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline underline-offset-4"
            >
              <Users className="h-3.5 w-3.5" />
              Team &amp; Access
            </button>
          )}
          {onSwitch && (
            <button
              type="button"
              onClick={onSwitch}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline underline-offset-4"
            >
              <Layers className="h-3.5 w-3.5" />
              Switch program
            </button>
          )}
        </div>
      </div>
      <div className="border-t border-border/60" />
    </div>
  );
}

/**
 * Program picker — the Home first screen for users holding BOTH programs
 * (D7 wireframe 1). Single-program users never see it.
 */
function ProgramPicker({ practiceCount, acoCount, onPick }: {
  practiceCount: number;
  acoCount: number;
  onPick: (program: 'mips' | 'app-plus') => void;
}) {
  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Welcome back</h1>
        <p className="text-muted-foreground mt-2">Choose a program to work in.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Card
          className="group cursor-pointer transition-all duration-200 hover:shadow-md hover:bg-accent/20"
          onClick={() => onPick('mips')}
        >
          <CardHeader>
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <BarChart3 className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl">MIPS</CardTitle>
            <CardDescription>Merit-based Incentive Payment System</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Quality reporting for your practices · {practiceCount} practice{practiceCount !== 1 ? 's' : ''}
            </p>
            <Button className="w-full">
              Enter MIPS <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
        <Card
          className="group cursor-pointer transition-all duration-200 hover:shadow-md hover:bg-accent/20"
          onClick={() => onPick('app-plus')}
        >
          <CardHeader>
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <Landmark className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl">APP Plus (MSSP)</CardTitle>
            <CardDescription>APM Performance Pathway Plus</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Quality reporting for your ACOs · {acoCount} ACO{acoCount !== 1 ? 's' : ''}
            </p>
            <Button className="w-full">
              Enter APP Plus <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

type HomeView =
  | 'loading'
  | 'choice'
  | 'single-org-form'
  | 'multi-org-form'
  | 'client-list'
  | 'program-picker'
  | 'aco-list';

/** D7 flow: program first, then entities. Pure — used for first paint AND sync. */
function resolveHomeView(
  authLoading: boolean,
  accessLoading: boolean,
  clients: Client[],
  hasBoth: boolean,
  hasApp: boolean,
): HomeView {
  // Wait for auth + grants on cold bootstrap (avoids dual-program wrong-screen
  // flash). On revisits both flags are already false, so this is a no-op —
  // unlike the old useState('loading') which spun on EVERY Home mount.
  if (authLoading || accessLoading) return 'loading';

  if (!clients || clients.length === 0) return 'choice';

  if (hasBoth) {
    const chosen = userStorage.getSessionItem('homeProgram');
    if (chosen === 'mips') return 'client-list';
    if (chosen === 'app-plus') return 'aco-list';
    return 'program-picker';
  }
  if (hasApp) return 'aco-list';
  return 'client-list';
}

export default function Home() {
  const navigate = useNavigate();
  const { token, user, clients, setClients, lastSelections, loading: authLoading, setSelectedClient, setSelectedPractice } = useAuth(); // Assuming clients are in AuthContext
  const { hasProgram, loading: accessLoading } = useProgramAccess();
  const { acos } = useAco();
  const hasMips = hasProgram('mips');
  const hasApp = hasProgram('app-plus');
  const hasBoth = hasMips && hasApp;

  // Seed from current context so navigating back to Home does NOT flash the
  // Loader2 (old code always mounted with view='loading', then effect fixed it).
  const [view, setView] = useState<HomeView>(() =>
    resolveHomeView(authLoading, accessLoading, clients, hasBoth, hasApp));
  const [teamSheetOpen, setTeamSheetOpen] = useState(false);

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  // Team & Access entry shows only for people who can actually manage a team.
  const canManageTeam =
    clients.some((c) => c.user_role === 'owner' || c.user_role === 'manager') ||
    (user?.role === 'admin' && clients.length > 0);

  // Sync when auth/grants/clients settle (first load) or change (registration).
  // Do not clobber in-progress onboarding form views.
  useEffect(() => {
    const next = resolveHomeView(authLoading, accessLoading, clients, hasBoth, hasApp);
    setView((prev) => {
      if (prev === 'single-org-form' || prev === 'multi-org-form') {
        // Stay on the form until registration adds a client (next leaves 'choice').
        if (next === 'choice' || next === 'loading') return prev;
        return next;
      }
      return next;
    });
  }, [clients, authLoading, accessLoading, hasBoth, hasApp]);

  const handlePickProgram = (program: 'mips' | 'app-plus') => {
    setHomeProgram(program); // notifies sidebar to show that program's nav
    setView(program === 'mips' ? 'client-list' : 'aco-list');
  };

  const handleBackToPicker = () => {
    setHomeProgram(null); // clears session → sidebar returns to Programs launcher
    setView('program-picker');
  };
  
  // Handler for form submission
  const handleRegistration = async (data: any) => {
    const response = await fetch(`${apiUrl}/api/clients/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to register organization.');
    }
    
    // Manually update the client list in the context after registration
    // This avoids needing a full page reload to see the new client
    const newClient = {
      id: result.client.id,
      client_alias: result.client.client_alias,
      account_id: result.client.account_id,
      practices: [result.practice]
    };
    setClients([...clients, newClient]);
    
    // Redirect after successful registration
    await handleSelectClient(newClient);
  };

  const handleSelectClient = async (client: Client) => {
    console.log('[handleSelectClient] called', { clientId: client.id, practiceCount: client.practices.length, path: window.location.pathname });
    
    // FIRST: Set localStorage immediately (synchronous)
    userStorage.setSessionItem('selectedClientId', client.id.toString());
    if (client.practices.length === 1) {
      userStorage.setSessionItem('selectedPracticeId', client.practices[0].id.toString());
      userStorage.setSessionItem('currentPractice', client.practices[0].id.toString());
    } else {
      localStorage.removeItem('selectedPracticeId');
      localStorage.removeItem('currentPractice');
    }
    
    console.log('[handleSelectClient] localStorage set', {
      clientId: userStorage.getSessionItem('selectedClientId'),
      practiceId: userStorage.getSessionItem('selectedPracticeId')
    });
    
    // SECOND: Set in AuthContext
    setSelectedClient(client);
    if (client.practices.length === 1) {
      setSelectedPractice(client.practices[0]);
    } else {
      setSelectedPractice(null);
    }
    
    // THIRD: Navigate (now localStorage is ready)
    navigate('/dashboard');
    
    // FOURTH: Background database save (async, doesn't block)
    try {
      await fetch(`${apiUrl}/api/user/selections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          clientId: client.id, 
          practiceId: client.practices.length === 1 ? client.practices[0].id : null,
        }),
      });
    } catch (error) {
      console.error("Failed to save client selection:", error);
    }
  };

  // Handler for selecting a specific practice from the hover popover
  const handleSelectPractice = async (client: Client, practice: Practice) => {
    console.log('[handleSelectPractice] called', { clientId: client.id, practiceId: practice.id });
    
    // Set localStorage immediately
    userStorage.setSessionItem('selectedClientId', client.id.toString());
    userStorage.setSessionItem('selectedPracticeId', practice.id.toString());
    userStorage.setSessionItem('currentPractice', practice.id.toString());
    
    // Set in AuthContext
    setSelectedClient(client);
    setSelectedPractice(practice);
    
    // Navigate to dashboard
    navigate('/dashboard');
    
    // Background database save
    try {
      await fetch(`${apiUrl}/api/user/selections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          clientId: client.id, 
          practiceId: practice.id,
        }),
      });
    } catch (error) {
      console.error("Failed to save practice selection:", error);
    }
  };

  // Render logic based on the current view state
  const renderContent = () => {
    switch (view) {
      case 'loading':
        return (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        );
      
      case 'choice':
        return <OnboardingChoice onSelect={(choice) => setView(choice === 'single-org' ? 'single-org-form' : 'multi-org-form')} />;
      
      case 'single-org-form':
        return <SingleOrgForm onSubmit={handleRegistration} onBack={() => setView('choice')} />;
        
      case 'multi-org-form':
        return <MultiOrgForm onSubmit={handleRegistration} onBack={() => setView('choice')} />;

      case 'program-picker':
        return (
          <ProgramPicker
            practiceCount={clients.reduce((n, c) => n + (c.practices?.filter(Boolean).length ?? 0), 0)}
            acoCount={acos.length}
            onPick={handlePickProgram}
          />
        );

      case 'aco-list':
        return (
          <div>
            {(hasBoth || canManageTeam) && (
              <SwitchProgramControl
                current="app-plus"
                onSwitch={hasBoth ? handleBackToPicker : undefined}
                onOpenTeam={canManageTeam ? () => setTeamSheetOpen(true) : undefined}
              />
            )}
            <AcoList />
          </div>
        );

      case 'client-list':
        return (
          <div>
            {(hasBoth || canManageTeam) && (
              <SwitchProgramControl
                current="mips"
                onSwitch={hasBoth ? handleBackToPicker : undefined}
                onOpenTeam={canManageTeam ? () => setTeamSheetOpen(true) : undefined}
              />
            )}
            <ClientListView 
              clients={clients}
              onSelectClient={handleSelectClient}
              onSelectPractice={handleSelectPractice}
              onRegisterNew={() => setView('choice')}
              onManageOrg={(clientId) => navigate(`/organization/${clientId}`)}
            />
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Home | VBCA</title>
        <meta name="description" content="Onboard and manage your organizations and practices for MIPS reporting." />
      </Helmet>
      <div className="container mx-auto py-12 px-4">
        {/* Access invitations show above every view — including onboarding, so a
            freshly-registered invitee sees their invite before creating an org. */}
        {view !== 'loading' && <PendingInvitations />}
        {renderContent()}
          </div>

      <TeamAccessSheet open={teamSheetOpen} onOpenChange={setTeamSheetOpen} />
    </>
  );
}



